import React, { ComponentProps, useCallback, useMemo } from 'react';
import Select, { SingleValue } from 'react-select';

import { SelectOption } from '../../../../../common/components/Select/models/select-option';
import { useTranslations } from '../../../../../contexts/LocalizationContext';
import { ContractType } from '../../models/enums/contract-type.enum';
import { mapLossContractTypeToSelectValueTranslationKey } from './utils/contract-type.helper';

type ContractTypeSelectProps = Omit<
  ComponentProps<typeof Select>,
  'options' | 'isLoading' | 'getOptionLabel' | 'getOptionValue' | 'placeholder' | 'onChange'
> & {
  selectedValue: ContractType | null;
  onSelect: (value: unknown) => void;
};

export const ContractTypeSelect = ({ isDisabled, name, selectedValue, onSelect, inputId }: ContractTypeSelectProps) => {
  const translations = useTranslations();
  const contractTypeSelectOptions = useMemo(
    () =>
      Object.values(ContractType).map((contractType) => ({
        value: contractType,
        label: translations(mapLossContractTypeToSelectValueTranslationKey(contractType)),
      })),
    [translations],
  );
  const handleChange = useCallback((v: SingleValue<SelectOption>) => onSelect(v?.value), [onSelect]);
  const handleOptionLabel = useCallback((option: SelectOption) => option.label, []);
  const handleOptionValue = useCallback((option: SelectOption) => option.value, []);

  return (
    <Select<SelectOption>
      inputId={inputId}
      name={name}
      value={contractTypeSelectOptions.find((option) => option.value === selectedValue)}
      onChange={handleChange}
      options={contractTypeSelectOptions}
      isDisabled={isDisabled}
      getOptionLabel={handleOptionLabel}
      getOptionValue={handleOptionValue}
    />
  );
};
